import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { locationsApi, roomsApi } from '@/api/endpoints'
import type { Room } from '@/api/types'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { SelectField, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'

import { DEFAULT_COLOR, PALETTE } from '@/lib/palette'

interface Props {
  open: boolean
  onClose: () => void
  room: Room | null
}

interface FormState {
  location_id: string
  name: string
  description: string
  equipment: string
  capacity: number
  color: string
  sort_order: number
}

const EMPTY: FormState = {
  location_id: '',
  name: '',
  description: '',
  equipment: '',
  capacity: 1,
  color: DEFAULT_COLOR,
  sort_order: 100,
}

export function RoomFormModal({ open, onClose, room }: Props) {
  const queryClient = useQueryClient()
  const { notifySuccess } = useToast()
  const isEdit = room !== null

  const [form, setForm] = useState<FormState>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
    enabled: open,
  })

  const locations = locationsQuery.data?.items ?? []

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})
    setForm(
      room
        ? {
            location_id: room.location_id,
            name: room.name,
            description: room.description ?? '',
            equipment: room.equipment ?? '',
            capacity: room.capacity,
            color: room.color,
            sort_order: room.sort_order,
          }
        : { ...EMPTY, location_id: locations[0]?.id ?? '' },
    )
    // Lista oddziałów dociąga się asynchronicznie; pierwszy wybór ustawiamy
    // dopiero gdy jest znany — stąd zależność od jej długości.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, room, locations.length])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const mutation = useMutation({
    mutationFn: () => {
      const base = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        equipment: form.equipment.trim() || null,
        capacity: form.capacity,
        color: form.color,
        sort_order: form.sort_order,
      }
      // Sala jest zasobem oddziału — przeniesienie zerwałoby historię rezerwacji.
      return isEdit
        ? roomsApi.update(room.id, base)
        : roomsApi.create({ ...base, location_id: form.location_id })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['rooms'] })
      notifySuccess(isEdit ? 'Zapisano zmiany sali.' : 'Sala została dodana.')
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zapisać sali.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    mutation.mutate()
  }

  const canSubmit = form.name.trim() !== '' && (isEdit || form.location_id !== '')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edytuj salę' : 'Nowa sala'}
      description={
        // Przy edycji oddziału nie da się zmienić, więc nie stoi tu jako
        // nieaktywne pole — sama informacja zostaje, bo warto wiedzieć,
        // której sali się dotyka.
        isEdit
          ? `Sala w oddziale ${room?.location.name ?? '—'}. Kolor wyznacza jej kolumnę w grafiku.`
          : 'Sala jest zasobem rezerwowanym w grafiku i należy do jednego oddziału.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="room-form"
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            {isEdit ? 'Zapisz zmiany' : 'Dodaj salę'}
          </Button>
        </>
      }
    >
      <form id="room-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        {!isEdit && (
          <SelectField
            label="Oddział"
            value={form.location_id}
            onChange={(event) => update('location_id', event.target.value)}
            options={locations.map((location) => ({
              value: location.id,
              label: location.name,
            }))}
            placeholder={locationsQuery.isPending ? 'Wczytywanie…' : 'Wybierz oddział'}
            error={fieldErrors.location_id}
            required
          />
        )}

        <TextField
          label="Nazwa sali"
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          placeholder="np. Sala Chopin"
          error={fieldErrors.name}
          required
          autoFocus
        />

        <TextField
          label="Opis"
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
          placeholder="np. Fortepian i klasyka"
          error={fieldErrors.description}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Liczba miejsc"
            type="number"
            min={1}
            max={200}
            value={form.capacity}
            onChange={(event) => update('capacity', Number(event.target.value) || 1)}
            error={fieldErrors.capacity}
            required
          />
          <SelectField
            label="Kolor w grafiku"
            value={form.color}
            onChange={(event) => update('color', event.target.value)}
            options={PALETTE}
            error={fieldErrors.color}
          />
        </div>

        <TextField
          label="Wyposażenie"
          value={form.equipment}
          onChange={(event) => update('equipment', event.target.value)}
          placeholder="np. fortepian Yamaha, 4 pulpity, lustro"
          error={fieldErrors.equipment}
        />

        <TextField
          label="Kolejność wyświetlania"
          type="number"
          min={0}
          max={9999}
          value={form.sort_order}
          onChange={(event) => update('sort_order', Number(event.target.value) || 0)}
          hint="Niższa wartość = wcześniejsza kolumna w grafiku."
          error={fieldErrors.sort_order}
        />
      </form>
    </Modal>
  )
}
